import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Shovel, MapPin, Users, Calendar, DollarSign, Filter, Loader2, Check, X, ChevronDown, ChevronUp, Eye, Plus, Pencil, UserCheck, Archive } from 'lucide-react';
import PostAnonAccountPrompt from '../components/PostAnonAccountPrompt';

const statusLabels = {
  en_cours: 'En cours',
  ouvert: 'Ouvert',
  termine: 'Terminé',
  archive: 'Archivé'
};

const statusColors = {
  en_cours: 'bg-yellow-100 text-yellow-800',
  ouvert: 'bg-green-100 text-green-800',
  termine: 'bg-blue-100 text-blue-800',
  archive: 'bg-gray-100 text-gray-800'
};

const TerrassementCard = ({ terrassement, sites, onParticipate, onEdit, onViewBailleurs, onArchive, userParticipations, isAgentOrAdmin }) => {
  const [expanded, setExpanded] = useState(false);
  const site = sites.find(s => s.site_id === terrassement.site_id);
  const hasParticipation = userParticipations.some(p => p.terrassement_id === terrassement.terrassement_id && !p.valide);
  
  const shortDesc = terrassement.description?.slice(0, 120);
  const needsExpand = terrassement.description?.length > 120;

  return (
    <Card className="card-hover bg-card border-none rounded-2xl overflow-hidden" data-testid={`terrassement-card-${terrassement.terrassement_id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-xl font-serif">{terrassement.titre || `Terrassement ${site?.nom || site?.ville || ''}`}</CardTitle>
          <Badge className={statusColors[terrassement.statut]}>
            {statusLabels[terrassement.statut]}
          </Badge>
        </div>
        {isAgentOrAdmin && (
          <div className="flex flex-wrap gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => onEdit(terrassement)}>
              <Pencil className="w-3 h-3 mr-1" /> Modifier
            </Button>
            <Button size="sm" variant="outline" onClick={() => onViewBailleurs(terrassement)}>
              <UserCheck className="w-3 h-3 mr-1" /> Bailleurs
            </Button>
            {terrassement.statut !== 'archive' && (
              <Button size="sm" variant="outline" className="text-orange-600" onClick={() => onArchive(terrassement)}>
                <Archive className="w-3 h-3 mr-1" /> {terrassement.statut === 'termine' ? 'Archiver' : 'Terminer'}
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground">
          {expanded ? terrassement.description : shortDesc}
          {needsExpand && !expanded && '...'}
          {needsExpand && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="ml-2 text-primary hover:underline inline-flex items-center gap-1"
            >
              {expanded ? <>Voir moins <ChevronUp className="w-3 h-3" /></> : <>Voir plus <ChevronDown className="w-3 h-3" /></>}
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            <span>{site?.ville || 'Non spécifié'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            <span>{terrassement.bailleurs_count || 0} bailleur(s)</span>
          </div>
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-primary" />
            <span>Min: {terrassement.montant_minimum?.toLocaleString() || 'N/A'} FCFA</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            <span>{terrassement.duree_estimee || 'Non définie'}</span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-0 flex gap-2">
        {site && (
          <Link to={`/sites/${site.site_id}`} className="flex-1">
            <Button variant="outline" className="w-full">
              <Eye className="w-4 h-4 mr-2" /> Voir site
            </Button>
          </Link>
        )}
        <Button 
          className="flex-1 btn-pill"
          disabled={terrassement.statut !== 'ouvert' || hasParticipation}
          onClick={() => onParticipate(terrassement)}
        >
          {hasParticipation ? 'En attente' : 'Participer'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default function Terrassements() {
  const navigate = useNavigate();
  const { user, token, isAgentOrAdmin } = useAuth();
  const [terrassements, setTerrassements] = useState([]);
  const [sites, setSites] = useState([]);
  const [participations, setParticipations] = useState([]);
  const [allParticipations, setAllParticipations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [participating, setParticipating] = useState(false);
  const [selectedTerrassement, setSelectedTerrassement] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [montant, setMontant] = useState('');
  const [bailleursDialogOpen, setBailleursDialogOpen] = useState(false);
  const [selectedForBailleurs, setSelectedForBailleurs] = useState(null);
  
  // Create/Edit
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTerrassement, setEditingTerrassement] = useState(null);
  const [formData, setFormData] = useState({
    titre: '', description: '', site_id: '', montant_minimum: '', duree_estimee: '', statut: 'ouvert'
  });
  
  // Anonymous
  const [anonDialogOpen, setAnonDialogOpen] = useState(false);
  const [anonData, setAnonData] = useState({ nom: '', prenom: '', email: '', telephone: '', montant: '' });
  const [postPromptOpen, setPostPromptOpen] = useState(false);
  const [postPromptUserExists, setPostPromptUserExists] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({ statut: '', site_id: '' });

  const fetchData = async () => {
    try {
      const [terrRes, sitesRes] = await Promise.all([
        fetch(`${API_URL}/api/terrassements`),
        fetch(`${API_URL}/api/sites`)
      ]);
      
      setTerrassements(await terrRes.json());
      setSites(await sitesRes.json());

      if (user) {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const partRes = await fetch(`${API_URL}/api/participations-terrassement`, { headers, credentials: 'include' });
        if (partRes.ok) {
          const partData = await partRes.json();
          setParticipations(partData.filter(p => p.client_id === user.user_id));
          if (isAgentOrAdmin()) setAllParticipations(partData);
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [user, token]);

  const handleParticipate = (terrassement) => {
    setSelectedTerrassement(terrassement);
    setMontant(terrassement.montant_minimum?.toString() || '');
    if (!user) {
      setAnonData({ ...anonData, montant: terrassement.montant_minimum?.toString() || '' });
      setAnonDialogOpen(true);
    } else {
      setDialogOpen(true);
    }
  };

  const handleEdit = (terrassement) => {
    setEditingTerrassement(terrassement);
    setFormData({
      titre: terrassement.titre || '',
      description: terrassement.description || '',
      site_id: terrassement.site_id || '',
      montant_minimum: terrassement.montant_minimum?.toString() || '',
      duree_estimee: terrassement.duree_estimee || '',
      statut: terrassement.statut || 'ouvert'
    });
    setEditDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingTerrassement(null);
    setFormData({ titre: '', description: '', site_id: '', montant_minimum: '', duree_estimee: '', statut: 'ouvert' });
    setEditDialogOpen(true);
  };

  const handleArchive = async (terrassement) => {
    const newStatut = terrassement.statut === 'termine' ? 'archive' : 'termine';
    const action = newStatut === 'archive' ? 'archiver' : 'terminer';
    if (!window.confirm(`Voulez-vous ${action} ce terrassement ?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/terrassements/${terrassement.terrassement_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...terrassement, statut: newStatut })
      });
      if (!response.ok) throw new Error('Erreur');
      toast.success(`Terrassement ${action === 'archiver' ? 'archivé' : 'terminé'}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSaveTerrassement = async () => {
    setParticipating(true);
    try {
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
      const body = { ...formData, montant_minimum: parseFloat(formData.montant_minimum) || 0 };
      
      const url = editingTerrassement 
        ? `${API_URL}/api/terrassements/${editingTerrassement.terrassement_id}`
        : `${API_URL}/api/terrassements`;
      
      const response = await fetch(url, { method: editingTerrassement ? 'PUT' : 'POST', headers, body: JSON.stringify(body) });
      if (!response.ok) throw new Error('Erreur');
      
      toast.success(editingTerrassement ? 'Terrassement mis à jour' : 'Terrassement créé');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setParticipating(false);
    }
  };

  const handleViewBailleurs = (terrassement) => {
    setSelectedForBailleurs(terrassement);
    setBailleursDialogOpen(true);
  };

  const handleValidateParticipation = async (participationId, valide) => {
    try {
      const response = await fetch(`${API_URL}/api/participations-terrassement/${participationId}/${valide ? 'valider' : 'refuser'}`, {
        method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur');
      toast.success(valide ? 'Participation validée' : 'Participation refusée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleConfirmParticipation = async () => {
    if (!selectedTerrassement) return;
    setParticipating(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/terrassements/${selectedTerrassement.terrassement_id}/participer`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ montant: parseFloat(montant) })
      });

      if (!response.ok) throw new Error('Erreur');
      toast.success('Demande de participation envoyée');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setParticipating(false);
    }
  };

  const handleAnonParticipation = async () => {
    setParticipating(true);
    try {
      const response = await fetch(`${API_URL}/api/terrassements/${selectedTerrassement.terrassement_id}/participer-anonyme`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...anonData, montant: parseFloat(anonData.montant) })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur');
      }
      const data = await response.json();
      setAnonDialogOpen(false);
      setPostPromptUserExists(!!data.user_exists);
      setPostPromptOpen(true);
    } catch (error) {
      toast.error(error.message || 'Erreur');
    } finally {
      setParticipating(false);
    }
  };

  const filteredTerrassements = terrassements.filter(t => {
    if (filters.statut && t.statut !== filters.statut) return false;
    if (filters.site_id && t.site_id !== filters.site_id) return false;
    return true;
  });

  const bailleursForSelected = selectedForBailleurs 
    ? allParticipations.filter(p => p.terrassement_id === selectedForBailleurs.terrassement_id)
    : [];

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Shovel className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-bold font-serif">Terrassements</h1>
              <p className="text-muted-foreground">Investissez dans les travaux de viabilisation</p>
            </div>
          </div>
          
          {isAgentOrAdmin() && (
            <Button onClick={handleCreate} className="btn-pill">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un terrassement
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-8 p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs mb-1 block">Statut</Label>
              <Select value={filters.statut} onValueChange={(v) => setFilters({...filters, statut: v === 'all' ? '' : v})}>
                <SelectTrigger><Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs mb-1 block">Site</Label>
              <Select value={filters.site_id} onValueChange={(v) => setFilters({...filters, site_id: v === 'all' ? '' : v})}>
                <SelectTrigger><MapPin className="w-4 h-4 mr-2" /><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les sites</SelectItem>
                  {sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.nom || s.ville}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTerrassements.map(terrassement => (
            <TerrassementCard
              key={terrassement.terrassement_id}
              terrassement={terrassement}
              sites={sites}
              onParticipate={handleParticipate}
              onEdit={handleEdit}
              onViewBailleurs={handleViewBailleurs}
              onArchive={handleArchive}
              userParticipations={participations}
              isAgentOrAdmin={isAgentOrAdmin()}
            />
          ))}
        </div>

        {filteredTerrassements.length === 0 && (
          <div className="text-center py-12">
            <Shovel className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Aucun terrassement trouvé</p>
          </div>
        )}

        {/* Participation Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Participer au terrassement</DialogTitle>
              <DialogDescription>Montant minimum: {selectedTerrassement?.montant_minimum?.toLocaleString()} FCFA</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Montant de participation (FCFA)</Label>
              <Input type="number" value={montant} onChange={(e) => setMontant(e.target.value)} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleConfirmParticipation} disabled={participating || !montant}>
                {participating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Anonymous Dialog */}
        <Dialog open={anonDialogOpen} onOpenChange={setAnonDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Vos coordonnées</DialogTitle>
              <DialogDescription>Renseignez vos informations pour financer ce terrassement.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Prénom *</Label><Input value={anonData.prenom} onChange={(e) => setAnonData({...anonData, prenom: e.target.value})} data-testid="anon-prenom-input" /></div>
                <div><Label>Nom *</Label><Input value={anonData.nom} onChange={(e) => setAnonData({...anonData, nom: e.target.value})} data-testid="anon-nom-input" /></div>
              </div>
              <div><Label>Email *</Label><Input type="email" value={anonData.email} onChange={(e) => setAnonData({...anonData, email: e.target.value})} data-testid="anon-email-input" /></div>
              <div><Label>Téléphone *</Label><Input value={anonData.telephone} onChange={(e) => setAnonData({...anonData, telephone: e.target.value})} data-testid="anon-telephone-input" /></div>
              <div><Label>Montant (FCFA) *</Label><Input type="number" value={anonData.montant} onChange={(e) => setAnonData({...anonData, montant: e.target.value})} data-testid="anon-montant-input" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAnonDialogOpen(false)}>Annuler</Button>
              <Button
                onClick={handleAnonParticipation}
                disabled={participating || !anonData.nom || !anonData.prenom || !anonData.email || !anonData.telephone || !anonData.montant}
                data-testid="submit-anon-terrassement-btn"
              >
                {participating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Envoyer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <PostAnonAccountPrompt
          open={postPromptOpen}
          onOpenChange={setPostPromptOpen}
          userExists={postPromptUserExists}
          prefillNom={anonData.nom}
          prefillPrenom={anonData.prenom}
          prefillEmail={anonData.email}
          prefillTelephone={anonData.telephone}
        />

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editingTerrassement ? 'Modifier' : 'Nouveau terrassement'}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div><Label>Titre</Label><Input value={formData.titre} onChange={(e) => setFormData({...formData, titre: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Site *</Label>
                  <Select value={formData.site_id} onValueChange={(v) => setFormData({...formData, site_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.nom || s.ville}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select value={formData.statut} onValueChange={(v) => setFormData({...formData, statut: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Montant minimum (FCFA)</Label><Input type="number" value={formData.montant_minimum} onChange={(e) => setFormData({...formData, montant_minimum: e.target.value})} /></div>
                <div><Label>Durée estimée</Label><Input value={formData.duree_estimee} onChange={(e) => setFormData({...formData, duree_estimee: e.target.value})} placeholder="Ex: 3 mois" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSaveTerrassement} disabled={participating}>
                {participating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingTerrassement ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bailleurs Dialog */}
        <Dialog open={bailleursDialogOpen} onOpenChange={setBailleursDialogOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>Bailleurs - {selectedForBailleurs?.titre || 'Terrassement'}</DialogTitle></DialogHeader>
            <div className="py-4">
              {bailleursForSelected.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">Aucun bailleur</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Téléphone</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bailleursForSelected.map(p => (
                      <TableRow key={p.participation_id}>
                        <TableCell>{p.client_nom || 'N/A'}</TableCell>
                        <TableCell>{p.client_email || 'N/A'}</TableCell>
                        <TableCell>{p.client_telephone || 'N/A'}</TableCell>
                        <TableCell>{p.montant?.toLocaleString()} FCFA</TableCell>
                        <TableCell>
                          <Badge className={p.valide ? 'bg-green-100 text-green-800' : p.valide === false ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                            {p.valide ? 'Validé' : p.valide === false ? 'Refusé' : 'En attente'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {p.valide === null && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleValidateParticipation(p.participation_id, true)}><Check className="w-4 h-4" /></Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleValidateParticipation(p.participation_id, false)}><X className="w-4 h-4" /></Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
